import type { CompanyUser, User } from "@/schema"
import type { UserCapability } from "@/schema/user-capabilities"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"

interface UsersResponse {
  users: User[]
}

interface CompanyUsersResponse {
  users: CompanyUser[]
}

export function fetchUsers(context: ApiContext) {
  return apiContextFetch<UsersResponse>(context, "users?minimal=true")
}

interface UsersWithEmailsResponse {
  users: { id: string; full_name: string; all_emails: string[] }[]
}

export function fetchUsersWithEmails(context: ApiContext) {
  return apiContextFetch<UsersWithEmailsResponse>(context, "users?include_emails=true")
}

export function fetchCompanyUsers(context: ApiContext) {
  return apiContextFetch<CompanyUsersResponse>(context, "users/company_users")
}

export function fetchCurrentUser(context: ApiContext) {
  return apiContextFetch<User>(context, "user")
}

export function fetchCurrentUserWithLiveblocksAccessToken(context: ApiContext) {
  return apiContextFetch<User>(context, "user?include_liveblocks_access_token=true")
}

export function updateUserCapabilities(
  context: ApiContext,
  userId: string,
  capabilities: UserCapability[],
  expected_capabilities: UserCapability[]
) {
  return apiContextFetch<User>(context, `users/${userId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user: { capabilities, expected_capabilities } }),
  })
}

export function updateCurrentUser(
  context: ApiContext,
  updates: Partial<User> & { profilePictureFile?: File; remove_profile_picture?: boolean }
) {
  const { profilePictureFile, remove_profile_picture, ...userUpdates } = updates

  if (profilePictureFile) {
    const formData = new FormData()

    formData.append("user[profile_picture]", profilePictureFile)

    Object.entries(userUpdates).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(`user[${key}]`, value.toString())
      }
    })

    return apiContextFetch<User>(context, "user", {
      method: "PUT",
      body: formData,
      headers: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        "Content-Type": undefined as any,
      },
    })
  } else {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = { ...userUpdates }
    if (remove_profile_picture) {
      payload.remove_profile_picture = true
    }

    return apiContextFetch<User>(context, "user", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ user: payload }),
    })
  }
}
